import type { SearchLocationResult, FormattedSearchResult } from '../types';
import { getCountryFlag, extractCountryFromDisplayName } from './countryFlags.js';

/**
 * Location Search Service using OpenStreetMap's Nominatim API
 * Handles forward geocoding: text queries → coordinates and location data
 * Optimized for interactive search with debouncing, caching, and rate limiting
 */

export interface NominatimResult {
  place_id: number;
  licence: string;
  osm_type: string;
  osm_id: number;
  boundingbox: [string, string, string, string];
  lat: string;
  lon: string;
  display_name: string;
  class: string;
  type: string;
  importance: number;
  icon?: string;
  address?: {
    // Standard address components
    house_number?: string;
    road?: string;
    pedestrian?: string;
    footway?: string;
    cycleway?: string;
    suburb?: string;
    neighbourhood?: string;
    quarter?: string;
    city?: string;
    town?: string;
    village?: string;
    county?: string;
    state_district?: string;
    state?: string;
    country?: string;
    country_code?: string;
    postcode?: string;
    
    // Points of Interest
    amenity?: string;
    tourism?: string;
    leisure?: string;
    shop?: string;
    building?: string;
    office?: string;
    
    // Natural features
    natural?: string;
    peak?: string;
    water?: string;
    waterway?: string;
    
    // Transportation
    railway?: string;
    aeroway?: string;
    highway?: string;
    
    // Administrative
    municipality?: string;
    administrative?: string;
    
    // Keep all other possible fields from Nominatim
    [key: string]: string | undefined;
  };
}

// Type alias for backward compatibility
export type SearchResult = SearchLocationResult;
export type { FormattedSearchResult } from '../types';

// Use API proxy to avoid CORS issues  
const NOMINATIM_BASE_URL = import.meta.env.DEV
  ? 'http://localhost:3333/api'
  : '/api';

export class NominatimService {
  private static instance: NominatimService;
  private cache = new Map<string, SearchLocationResult[]>();
  private requestCounter = 0;
  private activeRequests = new Set<string>(); // Track active requests

  static getInstance(): NominatimService {
    if (!NominatimService.instance) {
      NominatimService.instance = new NominatimService();
    }
    return NominatimService.instance;
  }

  async search(query: string, limit: number = 5): Promise<SearchLocationResult[]> {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const normalizedQuery = query.trim().toLowerCase();
    
    // Check if this exact query is already being processed
    if (this.activeRequests.has(normalizedQuery)) {
      return [];
    }
    
    // Check cache first
    if (this.cache.has(normalizedQuery)) {
      return this.cache.get(normalizedQuery)!;
    }

    // Mark this request as active
    this.activeRequests.add(normalizedQuery);

    try {
      // Rate limiting: max 1 request per second
      this.requestCounter++;
      const requestId = this.requestCounter;
      
      await this.delay(100); // Small delay to avoid too frequent requests
      
      // If a newer request was made, cancel this one
      if (requestId !== this.requestCounter) {
        return [];
      }

      const params = new URLSearchParams({
        q: query,
        format: 'json',
        limit: limit.toString(),
        countrycodes: 'de,at,ch', // Focus on German-speaking countries
        'accept-language': 'de,en',
        addressdetails: '1',
        extratags: '1',
        namedetails: '1'
      });

      const response = await fetch(`${NOMINATIM_BASE_URL}/search?${params}`, {
        headers: {
          'User-Agent': 'CyclingBuddy/1.0 (cycling route planner)'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: NominatimResult[] = await response.json();
      
      // Transform to our format
      const results: SearchLocationResult[] = data.map(item => ({
        id: item.place_id,
        name: this.extractShortName(item.display_name),
        displayName: item.display_name,
        lat: parseFloat(item.lat),
        lon: parseFloat(item.lon),
        type: item.type,
        class: item.class,
        importance: item.importance,
        country: item.address?.country, // Extract structured country data
        address: item.address ? { ...item.address } : undefined // Copy full address object
      }));

      // Sort by importance (higher is better)
      results.sort((a, b) => b.importance - a.importance);

      // Cache the results
      this.cache.set(normalizedQuery, results);
      
      // Limit cache size
      if (this.cache.size > 100) {
        const firstKey = this.cache.keys().next().value;
        if (firstKey) {
          this.cache.delete(firstKey);
        }
      }
      return results;
    } catch (error) {
      console.error('Nominatim search error:', error);
      
      // Enhanced error handling for network issues
      if (error instanceof Error && 
          (error.message.includes('Failed to fetch') || error.message.includes('ECONNREFUSED') || 
           error.message.includes('ENETUNREACH') || error.message.includes('ENOTFOUND'))) {
        // Import and use error handler for network issues
        import('../services/errorHandler').then(({ handleApiError }) => {
          handleApiError(
            new Error('Geocoding search service (Nominatim) is not available. Please check if the geocoding proxy is running.'),
            'nominatim/search',
            'location_search',
            {
              query: query.substring(0, 100), // Limit query length for logging
              service: 'geocoding',
              errorType: 'network'
            }
          );
        });
      }
      
      return [];
    } finally {
      // Remove from active requests
      this.activeRequests.delete(normalizedQuery);
    }
  }

  private extractShortName(displayName: string): string {
    // Extract the main place name from the full display name
    const parts = displayName.split(',');
    if (parts.length > 0) {
      return parts[0].trim();
    }
    return displayName;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Format a search result for UI display
   * Contains all provider-specific logic for icons, text formatting, etc.
   */
  formatSearchResult(result: SearchLocationResult): FormattedSearchResult {
    return {
      id: result.id,
      displayName: result.displayName,
      lat: result.lat,
      lon: result.lon,
      icon: this.getLocationIcon(result),
      mainLine: this.getMainLine(result),
      subLine: this.getSubLine(result),
      countryFlag: getCountryFlag(result.country || extractCountryFromDisplayName(result.displayName))
    };
  }

  /**
   * Get appropriate icon for location type
   */
  private getLocationIcon(result: SearchLocationResult): string {
    // Check address object first for more precise categorization
    if (result.address) {
      const addr = result.address;
      
      // Amenities from address data
      if (addr.amenity) {
        switch (addr.amenity) {
          case 'restaurant':
          case 'cafe':
          case 'bar':
          case 'pub': return '🍽️';
          case 'fuel': return '⛽';
          case 'hospital': return '🏥';
          case 'school':
          case 'university': return '🏫';
          case 'bank': return '🏦';
          case 'hotel': return '🏨';
          case 'parking': return '🅿️';
          default: return '📍';
        }
      }
      
      // Tourism from address data
      if (addr.tourism) {
        switch (addr.tourism) {
          case 'attraction':
          case 'viewpoint': return '🎯';
          case 'hotel':
          case 'guest_house': return '🏨';
          case 'museum': return '🏛️';
          case 'information': return 'ℹ️';
          default: return '🎯';
        }
      }
      
      // Natural features from address data
      if (addr.natural) {
        switch (addr.natural) {
          case 'peak': return '⛰️';
          case 'water':
          case 'lake': return '💧';
          case 'forest': return '🌲';
          case 'beach': return '🏖️';
          case 'valley':
          case 'ridge': return '🏔️';
          case 'cave_entrance': return '🕳️';
          default: return '🌿';
        }
      }
      
      // Transportation from address data
      if (addr.railway) return '🚂';
      if (addr.aeroway) return '✈️';
      if (addr.highway) return '🛣️';
    }

    // Fallback to class/type based detection
    // Cities and settlements
    if (result.type === 'city' || result.type === 'town' || result.type === 'village') {
      return '🏙️';
    } 
    // Transportation buildings
    else if (result.class === 'building' && result.type === 'train_station') {
      return '🚂';
    }
    // Natural features - prioritize class + type combination for accuracy
    else if ((result.class === 'natural' && result.type === 'peak') || result.type === 'peak') {
      return '⛰️';
    }
    else if ((result.class === 'natural' && result.type === 'water') || 
             result.type === 'lake' || result.type === 'river' || result.type === 'water') {
      return '💧';
    }
    else if ((result.class === 'natural' && result.type === 'forest') || result.type === 'forest') {
      return '🌲';
    }
    else if (result.type === 'beach' || result.class === 'natural' && result.type === 'beach') {
      return '🏖️';
    }
    else if (result.class === 'natural' && (result.type === 'valley' || result.type === 'ridge')) {
      return '🏔️';
    }
    else if (result.class === 'natural' && result.type === 'cave_entrance') {
      return '🕳️';
    }
    // Infrastructure
    else if (result.class === 'highway' || result.type === 'highway' || result.type === 'street') {
      return '🛣️';
    }
    else if (result.class === 'railway' || result.type === 'railway') {
      return '🚂';
    }
    else if (result.class === 'aeroway' || result.type === 'aerodrome') {
      return '✈️';
    }
    // Amenities and POIs
    else if (result.class === 'amenity' || result.type === 'amenity') {
      return '📍';
    }
    else if (result.class === 'tourism' || result.type === 'tourism') {
      return '🎯';
    }
    else if (result.class === 'shop' || result.type === 'shop') {
      return '🏪';
    }
    else if (result.class === 'leisure') {
      return '🏞️';
    }
    // Historic sites
    else if (result.class === 'historic') {
      return '🏛️';
    }
    // Default
    return '📍';
  }

  /**
   * Get main line text (primary name/address)
   */
  private getMainLine(result: SearchLocationResult): string {
    // If no address data is available, fallback to name
    if (!result.address) {
      return result.name || result.displayName;
    }
    
    const addr = result.address;
    
    // Check what type of location this is
    const isAmenity = result.class === 'amenity' || result.class === 'shop' || result.class === 'tourism' || result.class === 'historic' || (result.class === 'building' && result.type === 'train_station') || result.class === 'railway' || result.class === 'aeroway';
    const isAddress = !!(addr.road || addr.pedestrian || addr.footway || addr.cycleway);
    const isPlace = result.type === 'city' || result.type === 'town' || result.type === 'village';
    
    // For cities, towns, villages themselves - show just the name
    if (isPlace) {
      return result.name || addr.city || addr.town || addr.village || result.displayName;
    }
    
    // For amenities: Primary = name
    if (isAmenity && result.name) {
      return result.name;
    }
    
    // For addresses without amenity name: Primary = street
    if (isAddress) {
      const street = addr.road || addr.pedestrian || addr.footway || addr.cycleway;
      let primary = '';
      
      if (street) {
        if (addr.house_number) {
          primary = `${street} ${addr.house_number}`;
        } else {
          primary = street;
        }
      }
      
      return primary || result.name || result.displayName;
    }
    
    // For natural features, POIs without full addresses
    if (result.name) {
      return result.name;
    }
    
    // Final fallback
    return result.name || result.displayName;
  }

  /**
   * Search with formatted results ready for UI display
   */
  async searchFormatted(query: string, limit: number = 5): Promise<FormattedSearchResult[]> {
    const results = await this.search(query, limit);
    return results.map(result => this.formatSearchResult(result));
  }

  /**
   * Get sub line text (address/context information)
   */
  private getSubLine(result: SearchLocationResult): string {
    // If no address data is available, return empty
    if (!result.address) {
      return '';
    }
    
    const addr = result.address;
    
    // Check what type of location this is
    const isAmenity = result.class === 'amenity' || result.class === 'shop' || result.class === 'tourism' || result.class === 'historic' || (result.class === 'building' && result.type === 'train_station') || result.class === 'railway' || result.class === 'aeroway';
    const isAddress = !!(addr.road || addr.pedestrian || addr.footway || addr.cycleway);
    const isPlace = result.type === 'city' || result.type === 'town' || result.type === 'village';
    const isAdministrative = result.class === 'boundary' && result.type === 'administrative';
    
    // For cities, towns, villages - always show postcode if available
    // For administrative boundaries - prefer state over postcode
    if (isPlace || isAdministrative) {
      const locality = addr.city || addr.town || addr.village;
      const postcode = addr.postcode;
      const state = addr.state || addr.state_district;
      const county = addr.county;
      
      // For administrative boundaries, prefer state information
      if (isAdministrative && state) {
        return county ? `${state}, ${county}` : state;
      }
      
      // For regular places, try to show postcode with locality
      if (postcode && locality && locality !== result.name) {
        return county ? `${postcode} ${locality}, ${county}` : `${postcode} ${locality}`;
      } else if (postcode) {
        return county ? `${postcode}, ${county}` : postcode;
      } else if (state) {
        // Fallback to state if no postcode
        return county ? `${state}, ${county}` : state;
      } else if (locality && locality !== result.name) {
        return county ? `${locality}, ${county}` : locality;
      } else if (county) {
        return county;
      }
      
      return '';
    }
    
    // For amenities: Secondary = address
    if (isAmenity && result.name) {
      const addressParts: string[] = [];
      
      // Add street and house number
      if (isAddress) {
        const street = addr.road || addr.pedestrian || addr.footway || addr.cycleway;
        if (street) {
          if (addr.house_number) {
            addressParts.push(`${street} ${addr.house_number}`);
          } else {
            addressParts.push(street);
          }
        }
      }
      
      // Add postcode and city
      const locality = addr.city || addr.town || addr.village;
      const postcode = addr.postcode;
      const county = addr.county;
      
      if (postcode && locality) {
        const locationPart = county ? `${postcode} ${locality}, ${county}` : `${postcode} ${locality}`;
        addressParts.push(locationPart);
      } else if (locality) {
        const locationPart = county ? `${locality}, ${county}` : locality;
        addressParts.push(locationPart);
      } else if (postcode) {
        const locationPart = county ? `${postcode}, ${county}` : postcode;
        addressParts.push(locationPart);
      } else if (county) {
        addressParts.push(county);
      }
      
      return addressParts.join(', ');
    }
    
    // For addresses without amenity name: Secondary = postcode + city
    if (isAddress) {
      const locality = addr.city || addr.town || addr.village;
      const postcode = addr.postcode;
      const county = addr.county;
      
      if (postcode && locality) {
        return county ? `${postcode} ${locality}, ${county}` : `${postcode} ${locality}`;
      } else if (locality) {
        return county ? `${locality}, ${county}` : locality;
      } else if (postcode) {
        return county ? `${postcode}, ${county}` : postcode;
      } else if (county) {
        return county;
      }
      
      return '';
    }
    
    // For natural features, POIs without full addresses - show context with postcode if available
    if (result.name) {
      const context = addr.city || addr.town || addr.village || addr.suburb || addr.neighbourhood;
      const postcode = addr.postcode;
      const state = addr.state || addr.state_district;
      const county = addr.county;
      
      // Try to include postcode in context
      if (postcode && context && context !== result.name && !result.name.includes(context)) {
        return county ? `${postcode} ${context}, ${county}` : `${postcode} ${context}`;
      } else if (context && context !== result.name && !result.name.includes(context)) {
        return county ? `${context}, ${county}` : context;
      } else if (postcode) {
        return county ? `${postcode}, ${county}` : postcode;
      } else if (state) {
        // Fallback to state for broader context
        return county ? `${state}, ${county}` : state;
      } else if (county) {
        return county;
      }
    }
    
    // Final fallback - prefer state over postcode for broader context
    const postcode = addr.postcode;
    const state = addr.state || addr.state_district;
    const county = addr.county;
    
    if (state) {
      return county ? `${state}, ${county}` : state;
    } else if (county) {
      return county;
    } else if (postcode) {
      return postcode;
    }
    
    return '';
  }
}

export const nominatimService = NominatimService.getInstance();
